import React, { useState } from 'react';
import { Check, X, ChevronRight } from 'lucide-react';

/**
 * PlanComparison - Detaillierter Abo-Vergleich für Basic, Plus und Pro
 * Zeigt alle Features übersichtlich in einer Tabelle (Desktop) bzw. Tabs (Mobile)
 */
const PlanComparison = () => {
  const [activeMobilePlan, setActiveMobilePlan] = useState('basic');

  // Feature-Kategorien mit allen Details
  const featureCategories = [
    {
      title: 'Sichtbarkeit & Ranking',
      features: [
        {
          name: 'Präsenz in der EventConnect-Suche',
          basic: true,
          plus: true,
          pro: true
        },
        {
          name: 'Sichtbarkeit in der Suche',
          basic: 'Standard',
          plus: 'Höher',
          pro: 'Beste Platzierung'
        },
        {
          name: 'Priorität in Empfehlungslisten',
          basic: 'Niedrig',
          plus: 'Mittel',
          pro: 'Hoch'
        },
        {
          name: 'Badge im Profil & Suchergebnissen',
          basic: false,
          plus: '"Empfohlen"',
          pro: '"Top-Anbieter"'
        }
      ]
    },
    {
      title: 'Profil & Medien',
      features: [
        {
          name: 'Sichtbares Profil mit Basisdaten',
          basic: true,
          plus: true,
          pro: true,
          description: 'Name, Kategorien, Standort, Beschreibung, Kontakt'
        },
        {
          name: 'Profilbild / Logo',
          basic: true,
          plus: true,
          pro: true
        },
        {
          name: 'Bildergalerie',
          basic: 'Bis 5 Bilder',
          plus: 'Bis 10 Bilder',
          pro: 'Bis 10 Bilder'
        },
        {
          name: 'Video im Profil',
          basic: false,
          plus: false,
          pro: '1 Video'
        },
        {
          name: 'Social-Media-Links',
          basic: true,
          plus: true,
          pro: true,
          description: 'Instagram, Website, etc.'
        }
      ]
    },
    {
      title: 'Erweiterte Profilfelder',
      features: [
        {
          name: 'Spezialgebiete / Event-Schwerpunkte',
          basic: false,
          plus: true,
          pro: true,
          description: 'z.B. Hochzeiten, Firmenfeiern'
        },
        {
          name: 'Angebots-Pakete',
          basic: false,
          plus: true,
          pro: true,
          description: 'Mit Namen, Beschreibung, Preis'
        },
        {
          name: 'Ausführliche Servicegebiete',
          basic: 'Einfach',
          plus: 'Detailliert',
          pro: 'Detailliert'
        },
        {
          name: 'Sprachen anzeigen',
          basic: false,
          plus: true,
          pro: true
        },
        {
          name: 'Verfügbarkeits-Hinweis',
          basic: false,
          plus: true,
          pro: true
        },
        {
          name: 'Referenz-Texte',
          basic: false,
          plus: true,
          pro: true
        },
        {
          name: 'Zertifikate',
          basic: false,
          plus: true,
          pro: true,
          description: 'Text + Bild'
        }
      ]
    },
    {
      title: 'B2B & Profi-Funktionen',
      features: [
        {
          name: 'B2B-Fokus im Profil',
          basic: false,
          plus: 'Eingeschränkt',
          pro: true
        },
        {
          name: 'Event-Größen / Kapazität',
          basic: false,
          plus: false,
          pro: true,
          description: 'Bis 100, 100-500, 500+ Gäste'
        },
        {
          name: 'Reisebereitschaft',
          basic: false,
          plus: false,
          pro: true,
          description: 'Regional, deutschlandweit, DACH'
        },
        {
          name: 'Unternehmensinfos',
          basic: false,
          plus: false,
          pro: true,
          description: 'Rechtsform, Teamgröße'
        },
        {
          name: 'B2B-Rechnungs-Hinweise',
          basic: false,
          plus: false,
          pro: true,
          description: 'USt-ID, Firmenrechnungen'
        },
        {
          name: 'Referenzkunden-Liste',
          basic: false,
          plus: false,
          pro: true
        },
        {
          name: 'Case Studies',
          basic: false,
          plus: false,
          pro: true
        },
        {
          name: 'Teamsektion',
          basic: false,
          plus: false,
          pro: true
        },
        {
          name: 'Downloadbare Dokumente',
          basic: false,
          plus: false,
          pro: true,
          description: 'Portfolio-PDF, Leistungsübersicht'
        }
      ]
    },
    {
      title: 'Statistiken & Insights',
      features: [
        {
          name: 'Profilaufrufe gesamt',
          basic: true,
          plus: true,
          pro: true
        },
        {
          name: 'Profilaufrufe letzte 30 Tage',
          basic: false,
          plus: true,
          pro: true
        },
        {
          name: 'Favoriten-Anzahl',
          basic: 'Gesamt',
          plus: 'Mit Zeitraum',
          pro: 'Mit Verlauf'
        },
        {
          name: 'Erweiterte Statistiken',
          basic: false,
          plus: false,
          pro: true,
          description: 'Zeitverlauf'
        }
      ]
    },
    {
      title: 'Kommunikation & Verwaltung',
      features: [
        {
          name: 'Anfragen von Nutzern erhalten',
          basic: true,
          plus: true,
          pro: true
        },
        {
          name: 'Anfrage-Übersicht & Chat',
          basic: true,
          plus: true,
          pro: true
        },
        {
          name: 'Kundenbewertungen',
          basic: true,
          plus: true,
          pro: true,
          description: 'Sterne + Kommentare'
        },
        {
          name: 'Partner-Funktion',
          basic: true,
          plus: true,
          pro: true,
          description: 'Andere Dienstleister verlinken'
        },
        {
          name: 'Rechnungen & Abrechnung',
          basic: true,
          plus: true,
          pro: true
        },
        {
          name: 'Kontoeinstellungen',
          basic: true,
          plus: true,
          pro: true
        },
        {
          name: 'E-Mail-Support',
          basic: true,
          plus: true,
          pro: true
        }
      ]
    }
  ];

  const planNames = {
    basic: {
      name: 'Basic', price: '16,99 €', period: 'Monat',
      tabActive: 'bg-[#2B2142] text-white shadow-lg',
      headerGradient: 'bg-gradient-to-r from-[#2B2142] to-[#2B2142]'
    },
    plus: {
      name: 'Plus', price: '29,99 €', period: 'Monat',
      tabActive: 'bg-purple-600 text-white shadow-lg',
      headerGradient: 'bg-gradient-to-r from-purple-500 to-purple-600'
    },
    pro: {
      name: 'Pro', price: '49,99 €', period: 'Monat',
      tabActive: 'bg-amber-600 text-white shadow-lg',
      headerGradient: 'bg-gradient-to-r from-amber-500 to-amber-600'
    }
  };

  // Render feature value
  const renderFeatureValue = (value) => {
    if (value === true) {
      return <Check className="w-5 h-5 text-green-600 mx-auto" />;
    }
    if (value === false) {
      return <X className="w-5 h-5 text-gray-300 mx-auto" />;
    }
    if (typeof value === 'string') {
      return <span className="text-sm text-gray-700 font-medium">{value}</span>;
    }
    return null;
  };

  // Mobile-only: renders just an icon (no text, no mx-auto)
  const renderMobileFeatureIcon = (value) => {
    if (value === true || typeof value === 'string') {
      return <Check className="w-5 h-5 text-green-600" />;
    }
    if (value === false) {
      return <X className="w-5 h-5 text-gray-300" />;
    }
    return null;
  };

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="container max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Abovergleich im Detail
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Vergleiche Basic, Plus und Pro und entscheide, welches Paket am besten zu deinem Event-Business passt.
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Table Header */}
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 w-2/5">
                    Features
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#2B2142] w-1/5">
                    <div className="flex flex-col items-center">
                      <span className="text-lg">Basic</span>
                      <span className="text-xs text-gray-500 mt-1">16,99 €/Monat</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-purple-600 w-1/5">
                    <div className="flex flex-col items-center">
                      <span className="text-lg">Plus</span>
                      <span className="text-xs text-gray-500 mt-1">29,99 €/Monat</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-amber-600 w-1/5">
                    <div className="flex flex-col items-center">
                      <span className="text-lg">Pro</span>
                      <span className="text-xs text-gray-500 mt-1">49,99 €/Monat</span>
                    </div>
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {featureCategories.map((category, catIndex) => (
                  <React.Fragment key={catIndex}>
                    {/* Category Header */}
                    <tr className="bg-gray-100">
                      <td colSpan="4" className="px-6 py-3 text-left font-bold text-gray-900 text-sm uppercase tracking-wide">
                        {category.title}
                      </td>
                    </tr>
                    {/* Category Features */}
                    {category.features.map((feature, featIndex) => (
                      <tr key={featIndex} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 font-medium">{feature.name}</div>
                          {feature.description && (
                            <div className="text-xs text-gray-500 mt-1">{feature.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {renderFeatureValue(feature.basic)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {renderFeatureValue(feature.plus)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {renderFeatureValue(feature.pro)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile View - Tabs */}
        <div className="lg:hidden">
          {/* Plan Tabs */}
          <div className="flex justify-center gap-2 mb-6">
            {['basic', 'plus', 'pro'].map((plan) => (
              <button
                key={plan}
                onClick={() => setActiveMobilePlan(plan)}
                className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                  activeMobilePlan === plan
                    ? planNames[plan].tabActive
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                <div>{planNames[plan].name}</div>
                <div className="text-xs mt-1 opacity-90">{planNames[plan].price}</div>
              </button>
            ))}
          </div>

          {/* Active Plan Details */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className={`${planNames[activeMobilePlan].headerGradient} p-6 text-white`}>
              <h3 className="text-2xl font-bold">{planNames[activeMobilePlan].name}</h3>
              <p className="text-3xl font-bold mt-2">
                {planNames[activeMobilePlan].price}
                <span className="text-sm font-normal ml-2">/ {planNames[activeMobilePlan].period}</span>
              </p>
            </div>

            {/* Features List */}
            <div className="p-4">
              {featureCategories.map((category, catIndex) => (
                <div key={catIndex} className="mb-6">
                  <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wide mb-3 px-2">
                    {category.title}
                  </h4>
                  <div className="space-y-2">
                    {category.features.map((feature, featIndex) => {
                      const value = feature[activeMobilePlan];

                      return (
                        <div key={featIndex} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                          <div className="flex-shrink-0 mt-0.5">
                            {renderMobileFeatureIcon(value)}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-sm font-medium text-gray-900">
                              {feature.name}
                              {typeof value === 'string' && (
                                <span className="font-normal text-gray-600">: {value}</span>
                              )}
                            </div>
                            {feature.description && (
                              <div className="text-xs text-gray-500 mt-1">{feature.description}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scroll Hint */}
          <div className="text-center mt-4 text-sm text-gray-500">
            Wechsle zwischen den Tabs, um alle Pläne zu vergleichen
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Bereit, durchzustarten? Wähle jetzt deinen Plan!
          </p>
          <a
            href="#plans"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            Jetzt Plan wählen
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default PlanComparison;
